import {
  FormHelperText,
  Modal,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { FieldArray, Form, Formik } from "formik";
import { putAPICall, getAPICall } from "lib/callApi";
import {
  GET_ALL_PERMISSION,
  GET_PERMISSION_PROFILE_GRANTS,
  EDIT_PERMISSION_PROFILE_GRANTS,
  USER_DETAIL,
  PERMISSIONS_NAME,
} from "lib/constants";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { loginUserAction } from "redux/slice/users";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import { checkPermission } from "utils/HelperFunctions";
import { checkUserModuleComponentEnabledById } from "utils/Modules";

function EditPermission(props) {
  const { open, setOpen, permissionProfileToEdit } = props;
  const [permissionsList, setPermissionsList] = useState([]);
  const [permissionFetchLoading, setPermissionFetchLoading] = useState(false);
  const [permissionProfilesGrants, setPermissionProfileGrants] = useState([]);
  const [permissionProfileGrantsFetchLoading, setPermissionProfileGrantsFetchLoading] =
    useState(false);
  const [initialValues, setInitialValues] = useState({
    permissions: [],
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [httpError, setHttpError] = useState({ error: false, message: "" });
  const user = useSelector((state) => state.loginUser.loginUserState);
  const modules = useSelector((state) => state.modules.enabledModules);
  const dispatch = useDispatch();

  const [controller] = useMaterialUIController();

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 600 ? "100%" : 700,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    p: 4,
    outline: "none",
    maxHeight: "90%",
    borderRadius: "0.25em",
  };

  const handleClose = () => {
    if (!submitLoading) {
      setOpen(false);
      setHttpError({ error: false, message: "" });
      setInitialValues({ permissions: [] });
      setPermissionsList([]);
      setPermissionProfileGrants([]);
    }
  };

  const isSwitchChecked = (permission, values) => {
    try {
      if (
        values.find((x) => x.permission === PERMISSIONS_NAME.ADMIN).allow &&
        permission.permission !== PERMISSIONS_NAME.INPUT_KPIS
      ) {
        return true;
      }
      return permission.allow;
    } catch (err) {
      return false;
    }
  };

  const isSwitchDisabled = (permission, values) => {
    try {
      if (permission.disabled && permission.moduleCheck) {
        return true;
      }

      if (
        values.find((x) => x.permission === PERMISSIONS_NAME.ADMIN).allow &&
        permission.permission !== PERMISSIONS_NAME.INPUT_KPIS &&
        permission.permission !== PERMISSIONS_NAME.ADMIN
      ) {
        return true;
      }
      return permission.disabled;
    } catch (err) {
      return false;
    }
  };

  const initialDisabledState = (permission) => {
    try {
      if (
        permission.permission === PERMISSIONS_NAME.ADMIN &&
        !checkPermission(PERMISSIONS_NAME.ADMIN, user.userPermission)
      ) {
        return { disabled: true };
      }

      if (permission.FK_modules_id && !permission.FK_modules_components_id) {
        return {
          disabled: !checkUserModuleComponentEnabledById(modules, permission.FK_modules_id),
          moduleCheck: true,
        };
      }

      if (permission.FK_modules_id && permission.FK_modules_components_id) {
        return {
          disabled: !checkUserModuleComponentEnabledById(
            modules,
            permission.FK_modules_id,
            permission.FK_modules_components_id
          ),
          moduleCheck: true,
        };
      }

      return { disabled: false };
    } catch {
      return { disabled: false };
    }
  };

  const fetchAllPermission = async () => {
    try {
      setPermissionFetchLoading(true);
      const data = await getAPICall(GET_ALL_PERMISSION, true);

      if (data?.data?.success) {
        setPermissionsList(data.data.data.permissionsList);
      } else {
        handleClose();
        if (data.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: data?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
      setPermissionFetchLoading(false);
    } catch (error) {
      setPermissionFetchLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchPermissionProfileGrants = async () => {
    try {
      setPermissionProfileGrantsFetchLoading(true);
      const data = await getAPICall(GET_PERMISSION_PROFILE_GRANTS, true, {
        permissionProfileId: permissionProfileToEdit.id,
      });

      if (data?.data?.success) {
        setPermissionProfileGrants(data.data.data.permissionsList);
      } else {
        handleClose();
        if (data.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: data?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
      setPermissionProfileGrantsFetchLoading(false);
    } catch (error) {
      setPermissionProfileGrantsFetchLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (open) {
      fetchAllPermission();
      fetchPermissionProfileGrants();
    }
    return () => {
      setInitialValues({ permissions: [] });
      setPermissionsList([]);
      setPermissionProfileGrants([]);
    };
  }, [open]);

  useEffect(() => {
    if (permissionsList.length > 0) {
      const permissionProfileGrantsWithCompanyOverrides = Object.values(
        permissionProfilesGrants?.reduce((dict, permissionProfileGrant) => {
          if (!dict[permissionProfileGrant.FK_permissions_id]) {
            dict[permissionProfileGrant.FK_permissions_id] = permissionProfileGrant;
          }
          // We hit this if we already found a system default but the company had an override
          if (
            dict[permissionProfileGrant.FK_permissions_id] &&
            permissionProfileGrant.FK_companies_id
          ) {
            dict[permissionProfileGrant.FK_permissions_id] = permissionProfileGrant;
          }

          return dict;
        }, {})
      );

      const tempPermissions = permissionsList?.map((perm) => ({
        ...perm,
        allow: !!permissionProfileGrantsWithCompanyOverrides?.find(
          (perm1) =>
            perm1.FK_permissions_id === perm.id &&
            perm1.FK_permission_profiles_id === permissionProfileToEdit.id
        )?.allow,
        ...initialDisabledState(perm),
      }));

      setInitialValues((prev) => ({ ...prev, permissions: tempPermissions }));
    }
  }, [permissionsList, permissionProfileGrantsFetchLoading, permissionProfileToEdit]);

  const handleSubmit = async (data) => {
    const changedPermissions = data.permissions?.filter((perm) => {
      const perm1 = initialValues.permissions?.find((perm2) => perm2.id === perm.id);
      if (perm1.allow !== perm.allow) {
        return true;
      }
      return false;
    });

    const payload = {
      permissionProfileId: permissionProfileToEdit.id,
      changedPermissions,
    };
    try {
      setSubmitLoading(true);
      const response = await putAPICall(EDIT_PERMISSION_PROFILE_GRANTS, true, payload);
      if (response?.data?.success) {
        if (payload.permissionProfileId === user.permissionProfileId) {
          const result = await getAPICall(USER_DETAIL, true);
          if (result?.data?.success) {
            dispatch(loginUserAction.getLoginUserData(result?.data?.data));
          }
        }

        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message,
          }),
          {
            variant: "custom",
          }
        );
      } else {
        if (response.response.status === 403) {
          handleClose();
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: response?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
        setHttpError({
          error: true,
          message: response?.response?.data?.message || "Something Went Wrong",
        });
      }
      setSubmitLoading(false);
    } catch (error) {
      handleClose();
      setSubmitLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative" }}>
          <Loader loading={permissionFetchLoading || permissionProfileGrantsFetchLoading} />
          <MDTypography variant="h4">Edit Role Permission</MDTypography>
          <MDTypography
            variant="h5"
            mb={2}
            sx={{
              fontWeight: 400,
              color: "#8595af",
            }}
          >
            {permissionProfileToEdit?.role || ""}
          </MDTypography>

          <MDBox sx={{ position: "relative" }}>
            <Formik
              enableReinitialize
              validateOnChange
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, dirty }) => (
                <Form>
                  <FieldArray
                    name="permissions"
                    render={() => (
                      <>
                        <TableContainer
                          component={Paper}
                          sx={{
                            maxHeight:
                              window.innerHeight < 800
                                ? 0.5 * window.innerHeight
                                : 0.6 * window.innerHeight,
                            userSelect: "none",
                            boxShadow: "none",
                            borderRadius: 0,
                          }}
                        >
                          <Table aria-label="simple table">
                            <TableHead sx={{ display: "table-header-group" }}>
                              <TableRow>
                                <TableCell width="250px">Permission</TableCell>
                                <TableCell align="center">Description</TableCell>
                                <TableCell align="center" />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {values?.permissions?.map((permission, index) => (
                                <TableRow hover key={permission.id}>
                                  <TableCell>{permission.displayName}</TableCell>
                                  <TableCell>
                                    {permission.disabled && permission.moduleCheck
                                      ? "Not available on current Alysio plan!"
                                      : permission.description}
                                  </TableCell>
                                  <TableCell sx={{ padding: "0.4rem 1rem" }} align="center">
                                    <Switch
                                      checked={isSwitchChecked(permission, values.permissions)}
                                      disabled={isSwitchDisabled(permission, values.permissions)}
                                      onChange={(event) => {
                                        setFieldValue(
                                          `permissions[${index}].allow`,
                                          event.target.checked
                                        );
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {httpError.error && (
                          <MDBox
                            sx={{
                              "& .MuiFormHelperText-root": {
                                color: "#d32f2f",
                                fontSize: "16px",
                                mt: 2,
                                pl: 1,
                              },
                            }}
                          >
                            {" "}
                            <FormHelperText>{httpError.message}</FormHelperText>
                          </MDBox>
                        )}
                      </>
                    )}
                  />
                  <MDBox sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
                    <MDButton
                      color="gray80"
                      size="small"
                      onClick={handleClose}
                      sx={{ mt: 1, mr: 1, position: "relative" }}
                    >
                      Cancel
                    </MDButton>
                    <MDButton
                      color="primaryRed"
                      size="small"
                      type="submit"
                      sx={{ mt: 1, position: "relative" }}
                      disabled={submitLoading || !dirty}
                    >
                      {submitLoading ? "Loading..." : "Update"}
                    </MDButton>
                  </MDBox>
                </Form>
              )}
            </Formik>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default EditPermission;
